import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { loadRazorpayScript } from '../utils/loadRazorpayScript';
import { PRICE_KUNDLI_QUESTION, PRICE_KUNDLI_QUESTION_OLD, PRICE_FULL_REPORT } from '../constants/pricingCopy';
import { useLanguage } from '../context/LanguageContext';
import { useFreeQuestionEligibility } from '../hooks/useFreeQuestionEligibility';
import StruckPrice from './StruckPrice';

const API_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_PRODUCTION_API_URL || 'https://api.kundlishastra.com')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5001');

const PENDING_Q_KEY = 'ks_pending_question';
const OPEN_ASK_TAB_KEY = 'ks_open_ask_tab';

const LOCKED_PREVIEW =
  'Aapki kundli ke hisaab se abhi ek important phase chal raha hai...';

/** Rotates while the answer is generating — feels like a real astrologer at work, not a loading spinner. */
const CONSULTING_MESSAGES = [
  '🔮 Grahon se salah li ja rahi hai…',
  '📖 Aapki kundli check ki ja rahi hai…',
  '🌙 Lagna, Dasha aur Nakshatra dekhe ja rahe hain…',
  '✨ Guruji aapka jawab taiyar kar rahe hain…',
];

function sameQuestion(a, b) {
  return (a || '').trim() === (b || '').trim();
}

/**
 * Shastra AI — strictly separated UI:
 * 1) LOCKED — pre-pay: question, blur, lock line, unlock CTA only
 * 2) UNLOCKED — post-pay: Q + A once, then primary “Ask New Question”
 * 3) HISTORY — “Your Previous Questions”, excludes current session row
 */
export default function AskKundliQuestionTab({
  userId,
  profileId,
  userName,
  userEmail,
  memberName,
  itemVariants,
  prefilledQuestion = '',
}) {
  const { language } = useLanguage();
  const [question, setQuestion] = useState('');
  const [askNewMode, setAskNewMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generatingAnswer, setGeneratingAnswer] = useState(false);
  const [error, setError] = useState('');
  /** Current paid / loaded answer session (not shown in “Previous Questions”) */
  const [activeEntry, setActiveEntry] = useState(null);
  const [history, setHistory] = useState([]);
  /** Backend-verified: has this account NOT yet used its one free question? UI hint only — server re-checks at submit time. */
  const { freeQuestionAvailable: freeEligible, setFreeQuestionAvailable: setFreeEligible } =
    useFreeQuestionEligibility(userId);
  /** True only while the free-question request itself is in flight (for “generating” panel copy). */
  const [freeFlowActive, setFreeFlowActive] = useState(false);
  /** Cycles through CONSULTING_MESSAGES while an answer is being generated. */
  const [consultingIndex, setConsultingIndex] = useState(0);
  const answerRegionRef = useRef(null);
  const pendingScrollRef = useRef(false);

  const t = question.trim();
  const p = (prefilledQuestion || '').trim();
  /**
   * User/profile has a “real” question for Shastra: 10+ chars.
   * Ask-new: only what they type counts (ignore profile from birth form).
   * Otherwise: typed OR profile-only (e.g. from home form), not partial 1–9 chars in box.
   */
  const canLock = askNewMode
    ? t.length >= 10
    : t.length >= 10 || (t.length === 0 && p.length >= 10);

  const draftForPayment = (askNewMode ? t : (t || p)).trim();

  const has_answer = !!(activeEntry && String(activeEntry.answer || '').trim());

  const displayLockedQuestion = askNewMode ? t : t || p;

  useEffect(() => {
    setQuestion('');
    setAskNewMode(false);
    setActiveEntry(null);
    setError('');
  }, [userId, profileId]);

  const fetchHistory = useCallback(async () => {
    if (!userId || !profileId) return;
    try {
      const res = await fetch(
        `${API_URL}/api/kundli-questions?userId=${encodeURIComponent(userId)}&profileId=${encodeURIComponent(profileId)}`
      );
      const data = await res.json();
      if (data.success && Array.isArray(data.items)) {
        setHistory(data.items);
      }
    } catch {
      /* ignore */
    }
  }, [userId, profileId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  /** Hydrate active Q&amp;A from DB when opening tab (no duplicate in “Previous”). */
  useEffect(() => {
    if (askNewMode) return;
    if (activeEntry) return;
    if (!canLock) return;
    const dq = displayLockedQuestion.trim();
    if (dq.length < 10) return;
    if (!history.length) return;
    for (const row of history) {
      if (row?.answer && row?.id && sameQuestion(row.question, dq)) {
        setActiveEntry({ id: row.id, question: row.question, answer: row.answer });
        return;
      }
    }
  }, [history, displayLockedQuestion, askNewMode, activeEntry, canLock]);

  /** Attach server row id when verify did not return kundliQuestionId yet. */
  useEffect(() => {
    if (!activeEntry?.answer || activeEntry.id || !history.length) return;
    const m = history.find(
      (h) => h?.id && sameQuestion(h.question, activeEntry.question) && h.answer === activeEntry.answer
    );
    if (m?.id) setActiveEntry((prev) => (prev ? { ...prev, id: m.id } : prev));
  }, [history, activeEntry]);

  useEffect(() => {
    const p = (prefilledQuestion || '').trim();
    if (!p || askNewMode) return;
    setQuestion((q) => (q.trim() ? q : p));
  }, [prefilledQuestion, profileId, askNewMode]);

  const previousQuestions = useMemo(() => {
    if (!activeEntry?.id) return history;
    return history.filter((row) => row.id !== activeEntry.id);
  }, [history, activeEntry?.id]);

  /**
   * intake (compose / first-time) | locked (blur preview, first Q only) | unlocked | generating
   * Second question: we keep activeEntry so has_answer stays true — never auto-switch to `locked` while typing; pay from intake.
   */
  const uiPhase = useMemo(() => {
    if (generatingAnswer) return 'generating';
    if (has_answer && !askNewMode) return 'unlocked';
    if (!has_answer && canLock) return 'locked';
    return 'intake';
  }, [generatingAnswer, has_answer, askNewMode, canLock]);

  useEffect(() => {
    if (!generatingAnswer) return;
    const t = window.setTimeout(() => {
      answerRegionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => window.clearTimeout(t);
  }, [generatingAnswer]);

  /** Rotate the “consulting the planets” copy so the wait feels alive, not stuck. */
  useEffect(() => {
    if (!generatingAnswer) {
      setConsultingIndex(0);
      return;
    }
    const iv = window.setInterval(() => {
      setConsultingIndex((i) => (i + 1) % CONSULTING_MESSAGES.length);
    }, 2600);
    return () => window.clearInterval(iv);
  }, [generatingAnswer]);

  useEffect(() => {
    if (generatingAnswer || !activeEntry?.answer || !pendingScrollRef.current) return;
    pendingScrollRef.current = false;
    const t = window.setTimeout(() => {
      answerRegionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 180);
    return () => window.clearTimeout(t);
  }, [generatingAnswer, activeEntry?.answer]);

  const openAskTabOnNextLoad = () => {
    try {
      sessionStorage.setItem(OPEN_ASK_TAB_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  /** Shared by both the free-question path and the post-payment verify handler. */
  const applyAnswerResult = (answerText, questionText, kId) => {
    try {
      sessionStorage.removeItem(PENDING_Q_KEY);
    } catch {
      /* ignore */
    }
    openAskTabOnNextLoad();
    pendingScrollRef.current = true;
    setActiveEntry({ id: kId, question: questionText, answer: answerText });
    setAskNewMode(false);
    fetchHistory();
  };

  const payAndAsk = async () => {
    const q = draftForPayment;
    if (q.length < 10) {
      setError('Kripya apna sawaal kam se kam 10 characters mein likhein.');
      return;
    }
    setError('');
    // UI hint only — real eligibility is re-checked by the backend below.
    const attemptingFree = freeEligible;
    setLoading(true);
    if (attemptingFree) {
      setGeneratingAnswer(true);
      setFreeFlowActive(true);
    }
    try {
      const orderRes = await fetch(`${API_URL}/api/payments/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          purchaseType: 'kundli_question',
          userId,
          familyMemberId: profileId,
          userName: userName || '',
          userEmail: userEmail || '',
          question: q,
          language,
        }),
      });
      const order = await orderRes.json();
      if (!orderRes.ok) {
        throw new Error(order.error || 'Order create nahi ho saka');
      }

      // First question free — backend already generated the answer, no Razorpay needed.
      if (order.free) {
        setLoading(false);
        setGeneratingAnswer(false);
        setFreeFlowActive(false);
        setFreeEligible(false);
        applyAnswerResult(order.kundliQuestionAnswer || '', q, order.kundliQuestionId != null ? order.kundliQuestionId : null);
        return;
      }

      // Backend disagreed with our optimistic free-eligibility guess (e.g. already
      // used in another tab) — fall back to the existing paid flow, unchanged.
      if (attemptingFree) {
        setGeneratingAnswer(false);
        setFreeFlowActive(false);
        setFreeEligible(false);
      }

      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error('Payment gateway load nahi ho saka. Dobara try karein.');

      const options = {
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        name: 'Kundli Shastra',
        description: `Shastra AI answer — ${memberName || 'your profile'}`,
        order_id: order.orderId,
        prefill: { name: userName || '', email: userEmail || '' },
        theme: { color: '#b45309' },
        handler: async (response) => {
          setLoading(false);
          setGeneratingAnswer(true);
          setError('');
          try {
            const vRes = await fetch(`${API_URL}/api/payments/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                purchaseId: order.purchaseId,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                language,
              }),
            });
            const verified = await vRes.json();
            if (!verified.success) {
              throw new Error(verified.error || 'Verification failed');
            }
            const ans = verified.kundliQuestionAnswer || '';
            const kId = verified.kundliQuestionId != null ? verified.kundliQuestionId : null;
            applyAnswerResult(ans, q, kId);
          } catch (e) {
            setError(e.message || 'Payment verify failed');
          } finally {
            setGeneratingAnswer(false);
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            setGeneratingAnswer(false);
          },
        },
      };

      const rz = new window.Razorpay(options);
      rz.open();
    } catch (e) {
      setError(e.message || 'Kuch galat ho gaya');
      setLoading(false);
      setGeneratingAnswer(false);
      setFreeFlowActive(false);
    }
  };

  const startNewQuestion = () => {
    // Keep last Q&A in state so has_answer remains true: avoids jumping to locked/blur on every 10+ chars while typing
    setAskNewMode(true);
    setQuestion('');
    setError('');
    try {
      sessionStorage.removeItem(PENDING_Q_KEY);
    } catch {
      /* ignore */
    }
  };

  const disclaimerBlock = (placementClass = 'mt-6') => (
    <aside
      className={`${placementClass} rounded-xl border border-amber-200/60 bg-gradient-to-br from-stone-50/95 to-amber-50/40 px-4 py-3.5 shadow-[inset_0_1px_0_rgba(255,255,255,.6)]`}
      role="note"
      aria-label="Shastra AI disclaimer"
    >
      <p className="text-[0.65rem] font-semibold uppercase tracking-[0.08em] text-amber-900/70 mb-2.5">
        Disclaimer
      </p>
      <div className="space-y-2.5 text-[13px] leading-relaxed text-stone-700">
        <p>Shastra AI aapki kundli ke adhar par personalised guidance deta hai.</p>
        <p>Yeh direction dene ke liye hai — final decision aapka hota hai.</p>
        <p>Financial, legal ya medical faisle se pehle qualified expert se salah lena uchit rahega.</p>
      </div>
    </aside>
  );

  return (
    <motion.div variants={itemVariants} className="space-y-6">
      {/* Generating: only status card */}
      {uiPhase === 'generating' && (
        <div
          ref={answerRegionRef}
          id="kundli-qna-answer-region"
          className="scroll-mt-[calc(0.75rem+env(safe-area-inset-top,0px))]"
        >
          <motion.div
            variants={itemVariants}
            role="status"
            aria-live="polite"
            aria-busy="true"
            className="ks-dash-panel p-8 rounded-2xl border-2 border-dashed border-amber-300/90 bg-gradient-to-br from-amber-50/95 via-white to-orange-50/80 shadow-[0_8px_30px_rgba(120,53,15,.1)]"
          >
            <div className="flex flex-col items-center justify-center gap-4 text-center">
              <div
                className="h-14 w-14 rounded-full border-4 border-amber-200 border-t-amber-800 animate-spin"
                aria-hidden
              />
              <div>
                <p className="text-base font-bold text-amber-950">
                  {freeFlowActive && <>🎁 Aapka pehla sawaal FREE hai — </>}
                  {CONSULTING_MESSAGES[consultingIndex]}
                </p>
                <p className="text-sm text-amber-800/90 mt-2 leading-relaxed">
                  Guruji aapki kundli ke hisaab se jawab taiyar kar rahe hain. Thoda wait karein (usually 10–40 sec).
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* 1) LOCKED */}
      {uiPhase === 'locked' && !generatingAnswer && (
        <div className="ks-dash-panel p-6 rounded-2xl border border-amber-200/80 shadow-[0_8px_30px_rgba(120,53,15,.08)] bg-gradient-to-br from-amber-50/90 via-white to-orange-50/50">
          <h2 className="text-lg font-bold text-amber-900 mb-4">Shastra AI</h2>
          <div className="space-y-4">
            {freeEligible ? (
              <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 px-3 py-2.5 text-sm font-semibold text-emerald-800 flex items-center gap-2">
                <span aria-hidden>🎁</span> Aapka pehla Shastra AI sawaal FREE hai — koi payment nahi!
              </div>
            ) : (
              <div className="rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2.5 text-sm font-medium text-amber-900 flex items-center gap-2">
                <span aria-hidden>✨</span>
                <span>
                  Every additional personalised answer is only <StruckPrice oldPrice={PRICE_KUNDLI_QUESTION_OLD} newPrice={PRICE_KUNDLI_QUESTION} />.
                </span>
              </div>
            )}
            <div className="rounded-xl border border-amber-200 bg-white/90 px-4 py-3 text-amber-950 text-[15px] leading-relaxed">
              {displayLockedQuestion}
            </div>
            <div
              className="relative overflow-hidden rounded-xl border border-amber-200/90 bg-stone-50/95 px-4 py-3 min-h-[5.5rem]"
              aria-hidden
            >
              <p
                className="text-sm leading-relaxed text-amber-900/80 select-none"
                style={{ filter: 'blur(3.5px)' }}
              >
                {LOCKED_PREVIEW}
              </p>
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-amber-50/10 via-amber-50/40 to-amber-100/55 pointer-events-none" />
            </div>
            <p className="text-sm font-semibold text-amber-900">Answer locked hai</p>
            {error && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
            )}
            <button
              type="button"
              onClick={payAndAsk}
              disabled={loading}
              className="w-full min-h-[48px] rounded-xl px-6 py-3.5 font-bold text-[#fffbeb] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-[15px]"
              style={{
                background: loading ? '#9ca3af' : 'linear-gradient(90deg,#78350f,#b45309)',
                boxShadow: loading ? 'none' : '0 6px 24px rgba(120,53,15,.35)',
              }}
            >
              {loading
                ? (freeEligible ? 'Aapka free answer taiyar ho raha hai…' : 'Payment khol rahe hain…')
                : freeEligible
                  ? (<>🎁 Get Your First Answer FREE</>)
                  : (
                    <>
                      Unlock Your Answer → <StruckPrice oldPrice={PRICE_KUNDLI_QUESTION_OLD} newPrice={PRICE_KUNDLI_QUESTION} className="text-[#fffbeb]" />{' '}
                      <span aria-hidden>🔮</span>
                    </>
                  )}
            </button>
          </div>
        </div>
      )}

      {/* 2) UNLOCKED — one answer, then primary CTA; no list here */}
      {uiPhase === 'unlocked' && activeEntry && (
        <div
          ref={answerRegionRef}
          id="kundli-qna-answer-region"
          className="scroll-mt-[calc(0.75rem+env(safe-area-inset-top,0px))] space-y-4"
        >
          <div className="ks-dash-panel p-6 rounded-2xl border border-emerald-200 bg-white">
            <p className="text-xs font-bold uppercase text-amber-800/80 mb-2">Aapka sawaal</p>
            <p className="text-sm text-amber-950/95 mb-4 border-l-2 border-amber-400 pl-2.5 leading-relaxed">
              {activeEntry.question}
            </p>
            <h3 className="text-lg font-bold text-emerald-900 mb-3 flex items-center gap-2">
              <span aria-hidden>✨</span> Aapka answer
            </h3>
            <div className="text-stone-800 whitespace-pre-wrap leading-relaxed text-[15px]">{activeEntry.answer}</div>
            <button
              type="button"
              onClick={startNewQuestion}
              className="mt-6 w-full min-h-[48px] rounded-xl px-6 py-3.5 font-bold text-[#fffbeb] shadow-lg text-[15px] transition hover:brightness-105"
              style={{
                background: 'linear-gradient(90deg,#78350f,#b45309)',
                boxShadow: '0 6px 24px rgba(120,53,15,.35)',
              }}
            >
              Ask New Question → <StruckPrice oldPrice={PRICE_KUNDLI_QUESTION_OLD} newPrice={PRICE_KUNDLI_QUESTION} className="text-[#fffbeb]" />{' '}
              <span aria-hidden>✨</span>
            </button>
          </div>
          {disclaimerBlock('mt-0')}
        </div>
      )}

      {/* Pichla answer (read-only) — naya sawaal likhne ke dauran blur nahi */}
      {askNewMode && activeEntry?.answer && (
        <div className="ks-dash-panel p-6 rounded-2xl border border-emerald-200/80 bg-white shadow-[0_4px_20px_rgba(5,100,50,.08)]">
          <p className="text-xs font-bold uppercase text-emerald-800/85 mb-2">Aapka pichla answer</p>
          <p className="text-sm text-amber-950/90 mb-3 border-l-2 border-amber-400 pl-2.5 leading-relaxed">
            <span className="text-stone-500 font-medium">Q.</span> {activeEntry.question}
          </p>
          <p className="text-xs font-bold uppercase text-stone-600 mb-1.5">Answer</p>
          <div className="text-stone-800 whitespace-pre-wrap text-[15px] leading-relaxed max-h-[min(60vh,22rem)] overflow-y-auto pr-1">
            {activeEntry.answer}
          </div>
        </div>
      )}

      {/* 3) INTAKE — first Q: under 10 chars, locked handles blur+pay. Naya Q: type here, pay with Unlock (no auto-lock) */}
      {uiPhase === 'intake' && !generatingAnswer && (
        <div className="ks-dash-panel p-6 rounded-2xl border border-amber-200/80 shadow-[0_8px_30px_rgba(120,53,15,.08)] bg-gradient-to-br from-amber-50/90 via-white to-orange-50/50">
          <h2 className="text-xl font-bold text-amber-900 mb-1">
            {askNewMode ? 'Apna naya sawaal' : 'Aap kya jaana chahte hain?'}
          </h2>
          <p className="text-sm text-amber-800/90 mb-3">
            {!askNewMode && freeEligible ? (
              <>✨ Your First Question is FREE</>
            ) : (
              <>
                Shastra AI — <StruckPrice oldPrice={PRICE_KUNDLI_QUESTION_OLD} newPrice={PRICE_KUNDLI_QUESTION} /> · payment ke baad hi naya answer milega
              </>
            )}
          </p>
          {!askNewMode && freeEligible ? (
            <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 px-3 py-2.5 mb-4 text-sm font-semibold text-emerald-800 flex items-center gap-2">
              <span aria-hidden>🎁</span> Get Your First Personalised Answer FREE — no payment required.
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2.5 mb-4 text-sm font-medium text-amber-900 flex items-center gap-2">
              <span aria-hidden>✨</span>
              <span>
                Personalised Answer — <StruckPrice oldPrice={PRICE_KUNDLI_QUESTION_OLD} newPrice={PRICE_KUNDLI_QUESTION} />
              </span>
            </div>
          )}
          <div className="border border-amber-200/80 rounded-xl p-3 mb-4 space-y-1.5 bg-white/50">
            <p className="text-xs font-bold uppercase tracking-wide text-amber-800/90">Important</p>
            <p className="text-sm text-amber-900/95">
              {!askNewMode && freeEligible ? (
                <>
                  Aapka pehla sawaal free hai — iske baad se <StruckPrice oldPrice={PRICE_KUNDLI_QUESTION_OLD} newPrice={PRICE_KUNDLI_QUESTION} /> lagega. Premium PDF ({PRICE_FULL_REPORT}) alag product hai — banner se order karein.
                </>
              ) : (
                <>
                  Payment ke baad hi answer generate hoga. Premium PDF ({PRICE_FULL_REPORT}) alag product hai — banner se order karein.
                </>
              )}
            </p>
          </div>
          <label htmlFor="kq-textarea" className="block text-sm font-semibold text-amber-900 mb-2">
            {askNewMode ? 'Naya sawaal yahan pura likhein' : 'Apna sawaal likhein'}
          </label>
          <textarea
            id="kq-textarea"
            value={question}
            onChange={(e) => {
              const v = e.target.value;
              setQuestion(v);
              try {
                if (v.trim()) sessionStorage.setItem(PENDING_Q_KEY, v);
                else sessionStorage.removeItem(PENDING_Q_KEY);
              } catch {
                /* ignore */
              }
            }}
            rows={5}
            maxLength={4000}
            placeholder={'Jaise: "Meri shaadi kab hogi?" — "Career change sahi rahega?" — "Paisa ruk kyun raha hai?"'}
            className="w-full rounded-xl border border-amber-200 bg-white/95 px-4 py-3 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-[15px] leading-relaxed"
            disabled={loading}
          />
          {askNewMode ? (
            <p className="text-xs text-stone-500 mt-1">
              Aram se poora sawaal likhein (10+ characters). Upar aapka pehle wala answer dikhai de raha hai. Jab ready hon, neeche Unlock se payment karein — beech me blur / lock nahi aayega.
              {' '}Every additional personalised answer is only <StruckPrice oldPrice={PRICE_KUNDLI_QUESTION_OLD} newPrice={PRICE_KUNDLI_QUESTION} />.
            </p>
          ) : (
            <p className="text-xs text-stone-500 mt-1">
              Kam se kam 10 characters — jaisi yeh puri ho, neeche aapka sawaal, blur preview aur unlock step dikhega.
              {' '}
              {freeEligible ? (
                <span className="font-semibold text-emerald-700">🎁 Your first Shastra AI answer is absolutely FREE.</span>
              ) : (
                <>Every additional personalised answer is only <StruckPrice oldPrice={PRICE_KUNDLI_QUESTION_OLD} newPrice={PRICE_KUNDLI_QUESTION} />.</>
              )}
            </p>
          )}
          {error && (
            <p className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
          )}
          {askNewMode ? (
            <button
              type="button"
              onClick={payAndAsk}
              disabled={loading || t.length < 10}
              className="mt-4 w-full min-h-[48px] rounded-xl px-6 py-3.5 font-bold text-[#fffbeb] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-[15px]"
              style={{
                background: loading || t.length < 10 ? '#9ca3af' : 'linear-gradient(90deg,#78350f,#b45309)',
                boxShadow: loading || t.length < 10 ? 'none' : '0 6px 24px rgba(120,53,15,.35)',
              }}
            >
              {loading ? 'Payment khol rahe hain…' : (
                <>
                  Unlock Your Answer → <StruckPrice oldPrice={PRICE_KUNDLI_QUESTION_OLD} newPrice={PRICE_KUNDLI_QUESTION} className="text-[#fffbeb]" />{' '}
                  <span aria-hidden>🔮</span>
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              disabled
              className="mt-4 w-full min-h-[48px] rounded-xl px-6 py-3.5 font-bold text-[15px] text-amber-50/95 bg-stone-400 cursor-not-allowed"
              title={freeEligible ? '10+ characters likhne ke baad aapko free answer milega' : '10+ characters likhne ke baad next step par unlock + payment hoga'}
            >
              {freeEligible
                ? (<>🎉 Get Your First Personalised Answer FREE</>)
                : (
                  <>
                    Apna sawaal poochhein → <StruckPrice oldPrice={PRICE_KUNDLI_QUESTION_OLD} newPrice={PRICE_KUNDLI_QUESTION} className="text-amber-50/95" />{' '}
                    <span aria-hidden>✨</span>
                  </>
                )}
            </button>
          )}
          {!(freeEligible && !askNewMode) && <p className="text-xs text-amber-800/80 mt-2.5">🔒 Razorpay</p>}
          {disclaimerBlock('mt-5')}
        </div>
      )}

      {/* History — hide while locked; show with intake + unlocked */}
      {previousQuestions.length > 0 && uiPhase !== 'locked' && uiPhase !== 'generating' && (
        <motion.div variants={itemVariants} className="ks-dash-panel p-6 rounded-2xl border border-stone-200 bg-stone-50/40">
          <h3 className="text-lg font-bold text-stone-800 mb-4">Your Previous Questions</h3>
          <ul className="space-y-6">
            {previousQuestions.map((row) => (
              <li key={row.id} className="border-b border-stone-200/90 pb-6 last:border-0 last:pb-0">
                <p className="text-xs text-stone-500 mb-1">
                  {row.created_at
                    ? new Date(row.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
                    : ''}
                </p>
                <p className="font-semibold text-amber-900 text-sm mb-2">
                  <span className="text-stone-500 font-medium">Q.</span> {row.question}
                </p>
                <p className="text-stone-800 whitespace-pre-wrap text-sm leading-relaxed">
                  <span className="text-stone-500 font-medium">A.</span> {row.answer}
                </p>
              </li>
            ))}
          </ul>
        </motion.div>
      )}
    </motion.div>
  );
}
